import { useState, useEffect, useCallback } from 'react';
import { emailSettingsAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Loader2, Mail, KeyRound, Eye, EyeOff, CheckCircle2, AlertTriangle, Send, ExternalLink, Users, UserCog, FileText } from 'lucide-react';

export default function EmailSettings() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [showKey, setShowKey] = useState(false);
  const [keyDirty, setKeyDirty] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [testEmail, setTestEmail] = useState('');
  const [form, setForm] = useState({
    api_key: '',
    sender_email: '',
    sender_name: '',
    enabled: false,
    notify_staff: true,
    notify_customers: false,
    assignment_email_subject: '',
    assignment_email_body_html: '',
  });

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await emailSettingsAPI.get();
      setSettings(res.data);
      setForm({
        api_key: '',
        api_region: res.data.api_region || 'us',
        sender_email: res.data.sender_email || '',
        sender_name: res.data.sender_name || '',
        enabled: !!res.data.enabled,
        notify_staff: res.data.notify_staff !== false,
        notify_customers: !!res.data.notify_customers,
        assignment_email_subject: res.data.assignment_email_subject || '',
        assignment_email_body_html: res.data.assignment_email_body_html || '',
      });
      setKeyDirty(false);
    } catch (err) {
      console.error('Failed to load email settings:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const [logs, setLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);

  const loadLogs = useCallback(async () => {
    try {
      setLogsLoading(true);
      const res = await emailSettingsAPI.logs(50);
      setLogs(res.data || []);
    } catch (err) {
      console.error('Failed to load email logs', err);
    } finally {
      setLogsLoading(false);
    }
  }, []);

  useEffect(() => { load(); loadLogs(); }, [load, loadLogs]);

  const handleSave = async () => {
    try {
      setSaving(true);
      const payload = {
        api_region: form.api_region || 'us',
        sender_email: form.sender_email || undefined,
        sender_name: form.sender_name || undefined,
        enabled: form.enabled,
        notify_staff: form.notify_staff,
        notify_customers: form.notify_customers,
        assignment_email_subject: form.assignment_email_subject || undefined,
        assignment_email_body_html: form.assignment_email_body_html || undefined,
      };
      if (keyDirty && form.api_key) payload.api_key = form.api_key;
      const res = await emailSettingsAPI.update(payload);
      setSettings(res.data);
      setKeyDirty(false);
      setForm((f) => ({ ...f, api_key: '' }));
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleClearKey = async () => {
    if (!window.confirm('Remove the saved API key? Email sending will be disabled until a new key is added.')) return;
    try {
      const res = await emailSettingsAPI.update({ api_key: '__clear__', enabled: false });
      setSettings(res.data);
      setForm((f) => ({ ...f, api_key: '', enabled: false }));
      setKeyDirty(false);
    } catch (err) {
      alert('Failed to clear key');
    }
  };

  const handleTest = async () => {
    if (!testEmail) return;
    try {
      setTesting(true);
      setTestResult(null);
      const res = await emailSettingsAPI.test(testEmail);
      setTestResult(res.data);
    } catch (err) {
      setTestResult({ status: 'error', error: err.response?.data?.detail || err.message });
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <Card className="bg-white border-slate-200"><CardContent className="py-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-cyan-500" /></CardContent></Card>
    );
  }

  const hasKey = settings?.api_key_set;
  const ready = hasKey && form.sender_email && form.enabled;

  return (
    <Card className="bg-white border-slate-200" data-testid="email-settings">
      <CardHeader>
        <CardTitle className="text-slate-800 flex items-center gap-2">
          <Mail className="w-5 h-5 text-cyan-500" />
          Email Settings
          {ready && <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 text-[11px]"><CheckCircle2 className="w-3 h-3 mr-1" />Active</Badge>}
          {!ready && <Badge className="bg-amber-100 text-amber-700 border-amber-200 text-[11px]"><AlertTriangle className="w-3 h-3 mr-1" />Not configured</Badge>}
        </CardTitle>
        <p className="text-sm text-slate-500">
          Configure <strong>Zepto Mail</strong> to send ticket notifications and calendar invitations. Get an API key from{' '}
          <a href="https://www.zoho.com/zeptomail/" target="_blank" rel="noopener noreferrer" className="text-cyan-600 hover:underline inline-flex items-center gap-1">
            zoho.com/zeptomail <ExternalLink className="w-3 h-3" />
          </a>{' '}— Mail Agents → API.
        </p>
      </CardHeader>
      <CardContent className="space-y-5">
        {/* Sender info */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label htmlFor="sender-email" className="text-slate-700">Sender email <span className="text-red-500">*</span></Label>
            <Input
              id="sender-email"
              type="email"
              value={form.sender_email}
              onChange={(e) => setForm({ ...form, sender_email: e.target.value })}
              placeholder="helpdesk@mvpit.ca"
              className="bg-white border-slate-200 h-11"
              data-testid="email-sender-input"
            />
            <p className="text-xs text-slate-400">Must be on a verified domain in your Zepto account.</p>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="sender-name" className="text-slate-700">Sender name</Label>
            <Input
              id="sender-name"
              value={form.sender_name}
              onChange={(e) => setForm({ ...form, sender_name: e.target.value })}
              placeholder="MVP IT Helpdesk"
              className="bg-white border-slate-200 h-11"
              data-testid="email-sender-name-input"
            />
          </div>
        </div>

        {/* API key */}
        <div className="space-y-1.5">
          <Label htmlFor="api-key" className="text-slate-700 flex items-center gap-1.5">
            <KeyRound className="w-4 h-4 text-slate-400" />
            Zepto Mail API key <span className="text-red-500">*</span>
          </Label>
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Input
                id="api-key"
                type={showKey ? 'text' : 'password'}
                value={form.api_key}
                onChange={(e) => { setForm({ ...form, api_key: e.target.value }); setKeyDirty(true); }}
                placeholder={hasKey ? `Saved (${settings.api_key_preview}). Type to replace.` : 'Zoho-enczapikey ...'}
                className="bg-white border-slate-200 h-11 pr-10 font-mono text-sm"
                data-testid="email-api-key-input"
              />
              <Button type="button" variant="ghost" size="icon" className="absolute right-1 top-1/2 -translate-y-1/2 h-9 w-9 text-slate-400" onClick={() => setShowKey(!showKey)} aria-label={showKey ? 'Hide key' : 'Show key'}>
                {showKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </Button>
            </div>
            {hasKey && (
              <Button type="button" variant="outline" className="rounded-full h-11 border-red-200 text-red-600 hover:bg-red-50" onClick={handleClearKey} data-testid="email-clear-key-btn">
                Remove key
              </Button>
            )}
          </div>
          {hasKey && !keyDirty && (
            <p className="text-xs text-emerald-600 flex items-center gap-1"><CheckCircle2 className="w-3 h-3" />Key on file. Leave blank to keep using it.</p>
          )}
          <p className="text-[11px] text-slate-500 mt-1">
            Tip: you can paste the full <code className="bg-slate-100 px-1 rounded">Zoho-enczapikey&nbsp;...</code> token — we&apos;ll strip the prefix for you.
          </p>
        </div>

        {/* API region — silently wrong region is the #1 "why isn't Zepto working" cause */}
        <div className="space-y-1.5">
          <Label htmlFor="api-region" className="text-slate-700">Zepto Mail region</Label>
          <select
            id="api-region"
            value={form.api_region || 'us'}
            onChange={(e) => setForm({ ...form, api_region: e.target.value })}
            className="h-11 w-full rounded-md border border-slate-200 bg-white px-3 text-sm"
            data-testid="email-region-select"
          >
            <option value="us">United States — api.zeptomail.com (default)</option>
            <option value="eu">European Union — api.zeptomail.eu</option>
            <option value="in">India — api.zeptomail.in</option>
          </select>
          <p className="text-[11px] text-slate-500">
            Must match the data center of your Zepto Mail account. If your Zepto dashboard URL contains <code className="bg-slate-100 px-1 rounded">.eu</code> or <code className="bg-slate-100 px-1 rounded">.in</code>, pick the matching region — otherwise every send silently 401s.
          </p>
        </div>

        {/* Enable */}
        <div className="flex items-center gap-3 p-3 rounded-lg bg-slate-50 border border-slate-200">
          <Switch
            id="email-enabled"
            checked={form.enabled}
            onCheckedChange={(v) => setForm({ ...form, enabled: v })}
            disabled={!hasKey && !keyDirty}
            data-testid="email-enabled-switch"
          />
          <Label htmlFor="email-enabled" className="cursor-pointer text-slate-700">
            Enable email sending
            <span className="block text-xs text-slate-500 mt-0.5">Master switch — when off, every email is silently skipped.</span>
          </Label>
        </div>

        {/* Recipient-side toggles — split so clients and staff are controlled independently */}
        <div className="rounded-lg border border-slate-200 divide-y divide-slate-100">
          <div className="flex items-center gap-3 p-3">
            <Switch
              id="notify-staff"
              checked={form.notify_staff}
              onCheckedChange={(v) => setForm({ ...form, notify_staff: v })}
              data-testid="notify-staff-switch"
            />
            <Label htmlFor="notify-staff" className="cursor-pointer text-slate-700 flex-1">
              <span className="inline-flex items-center gap-2 font-medium">
                <UserCog className="w-4 h-4 text-cyan-600" /> Staff notifications
              </span>
              <span className="block text-xs text-slate-500 mt-0.5">
                Send emails to agents / admins (ticket assignment notifications).
              </span>
            </Label>
          </div>
          <div className="flex items-center gap-3 p-3">
            <Switch
              id="notify-customers"
              checked={form.notify_customers}
              onCheckedChange={(v) => setForm({ ...form, notify_customers: v })}
              data-testid="notify-customers-switch"
            />
            <Label htmlFor="notify-customers" className="cursor-pointer text-slate-700 flex-1">
              <span className="inline-flex items-center gap-2 font-medium">
                <Users className="w-4 h-4 text-slate-500" /> Customer notifications
              </span>
              <span className="block text-xs text-slate-500 mt-0.5">
                Send emails to customers (Work Order PDFs, status changes). Leave off to keep client email silent.
              </span>
            </Label>
          </div>
        </div>

        {/* Assignment email template editor (staff-facing) */}
        <div className="rounded-lg border border-slate-200 p-4 space-y-3">
          <div className="flex items-center gap-2">
            <FileText className="w-4 h-4 text-cyan-600" />
            <h3 className="text-sm font-semibold text-slate-700">Ticket assignment email template</h3>
          </div>
          <p className="text-xs text-slate-500">
            Sent to every tech who&apos;s just been assigned to a ticket (requires <em>Staff notifications</em> above).
            Placeholders: <code className="px-1 bg-slate-100 rounded">{'{agent_name}'}</code>,{' '}
            <code className="px-1 bg-slate-100 rounded">{'{ticket_number}'}</code>,{' '}
            <code className="px-1 bg-slate-100 rounded">{'{subject}'}</code>,{' '}
            <code className="px-1 bg-slate-100 rounded">{'{priority}'}</code>,{' '}
            <code className="px-1 bg-slate-100 rounded">{'{customer_name}'}</code>,{' '}
            <code className="px-1 bg-slate-100 rounded">{'{description}'}</code>,{' '}
            <code className="px-1 bg-slate-100 rounded">{'{ticket_url}'}</code>,{' '}
            <code className="px-1 bg-slate-100 rounded">{'{assigned_by_name}'}</code>.
          </p>
          <div className="space-y-1.5">
            <Label className="text-xs">Subject</Label>
            <Input
              value={form.assignment_email_subject}
              onChange={(e) => setForm({ ...form, assignment_email_subject: e.target.value })}
              placeholder="Ticket {ticket_number} assigned to you — {subject}"
              className="bg-white border-slate-200 h-10"
              data-testid="assignment-subject-input"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">HTML body</Label>
            <Textarea
              value={form.assignment_email_body_html}
              onChange={(e) => setForm({ ...form, assignment_email_body_html: e.target.value })}
              placeholder="<p>Hi {agent_name}, …</p>"
              rows={6}
              className="bg-white border-slate-200 font-mono text-xs"
              data-testid="assignment-body-input"
            />
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-2 justify-end pt-2 border-t border-slate-100">
          <Button onClick={handleSave} disabled={saving} className="rounded-full bg-cyan-500 hover:bg-cyan-600 h-11 px-6" data-testid="email-save-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save settings'}
          </Button>
        </div>

        {/* Test send */}
        {settings?.api_key_set && form.enabled && (
          <div className="border-t border-slate-200 pt-5 space-y-3">
            <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2"><Send className="w-4 h-4 text-cyan-500" />Send test email</h3>
            <div className="flex flex-col sm:flex-row gap-2">
              <Input
                type="email"
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
                placeholder="you@example.com"
                className="bg-white border-slate-200 h-11"
                data-testid="email-test-to-input"
              />
              <Button type="button" onClick={handleTest} disabled={testing || !testEmail} className="rounded-full h-11 bg-slate-800 hover:bg-slate-900 text-white px-5" data-testid="email-test-send-btn">
                {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Send test'}
              </Button>
            </div>
            {testResult && (
              <div className={`p-3 rounded-lg text-sm border ${testResult.status === 'success' ? 'bg-emerald-50 border-emerald-200 text-emerald-800' : 'bg-red-50 border-red-200 text-red-800'}`} data-testid="email-test-result">
                <strong className="block mb-1">{testResult.status === 'success' ? 'Test email sent' : `Test failed${testResult.code ? ` (${testResult.code})` : ''}`}</strong>
                <code className="text-xs block whitespace-pre-wrap break-all">{JSON.stringify(testResult.data || testResult.error || testResult, null, 2).slice(0, 400)}</code>
              </div>
            )}
          </div>
        )}

        {/* Recent activity */}
        <div className="border-t border-slate-200 pt-5 space-y-3" data-testid="email-activity-panel">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
              <FileText className="w-4 h-4 text-cyan-500" />
              Recent email activity
              <span className="text-xs text-slate-400 font-normal">— last 50 dispatch attempts</span>
            </h3>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={loadLogs}
              disabled={logsLoading}
              className="rounded-full"
              data-testid="email-logs-refresh"
            >
              {logsLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Refresh'}
            </Button>
          </div>

          {logs.length === 0 ? (
            <p className="text-sm text-slate-500 italic">
              {logsLoading ? 'Loading…' : 'No email attempts logged yet. Assign a ticket to an agent with an email on file, then refresh.'}
            </p>
          ) : (
            <div className="border border-slate-200 rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-xs uppercase text-slate-500">
                  <tr>
                    <th className="px-3 py-2 text-left">When</th>
                    <th className="px-3 py-2 text-left">Recipient</th>
                    <th className="px-3 py-2 text-left">Subject</th>
                    <th className="px-3 py-2 text-left">Status</th>
                    <th className="px-3 py-2 text-left">Detail</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {logs.map((log) => {
                    const tone = log.status === 'success'
                      ? 'bg-emerald-100 text-emerald-700 border-emerald-200'
                      : log.status === 'error'
                        ? 'bg-red-100 text-red-700 border-red-200'
                        : 'bg-amber-100 text-amber-700 border-amber-200';
                    const detail = log.error
                      ? (typeof log.error === 'string' ? log.error : JSON.stringify(log.error))
                      : (log.reason || '');
                    return (
                      <tr key={log.id} className="hover:bg-slate-50" data-testid={`email-log-row-${log.id}`}>
                        <td className="px-3 py-2 text-slate-600 whitespace-nowrap text-xs">{new Date(log.created_at).toLocaleString()}</td>
                        <td className="px-3 py-2 text-slate-800 truncate max-w-[180px]" title={log.recipient_email}>{log.recipient_email}</td>
                        <td className="px-3 py-2 text-slate-700 truncate max-w-[260px]" title={log.subject}>{log.subject}</td>
                        <td className="px-3 py-2">
                          <Badge className={`${tone} text-[11px] border`}>{log.status}</Badge>
                        </td>
                        <td className="px-3 py-2 text-xs text-slate-500 truncate max-w-[260px]" title={detail}>{detail || '—'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
